import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, IconButton, Alert as MaterialAlert, Typography } from '@mui/material';
import { FC, useRef, useState } from 'react';
import PinInput from 'react-pin-input';
import { Alert } from '../../../shared/lib/common/Alert';
import { PageHeading } from '../../../shared/lib/common/PageHeading';
import { withTrademark } from '../../../shared/lib/common/Tokenizer';
import palette from '../../../shared/lib/theme/palette';
import { useAppContext } from '../app/contexts/AppContext';
import { VerifyOtpProps, sendOtpV2Type } from '../app/interfaceTypes';
import { ActionType } from '../app/reducer/AppContextReducer';
import { OtpVerification, useVerifyOtpMutation } from '../generated';
import { CustomPinInput } from './CustomPinInput';
import { Timer } from './common/Timer';
import { MSG_OTP_VERIFIED, MSG_PHONE_NUMBER_UPDATED } from '../app/constants';
import {
  ONBOARDING_ADD_INFO_STRINGS,
  LABEL_VERIFICATION_CODE,
  MSG_VERIFICATION_CODE_EXPIRED,
  MSG_VERIFICATION_CODE_EXPIRES_IN,
  NOTE_VERIFICATION_REFRESH_PAGE,
  MSG_DONT_HAVE_CODE,
  BUTTON_TEXT_RESEND_CODE,
  MSG_DEV_OTP_NOTICE,
} from '../../../shared/lib/constants';

export const VerifyOtp: FC<VerifyOtpProps> = ({ infoSteps, handleNext, displayPhoneNumber, sendOtp, resetPhone, emailEditClose, isTpin, code }) => {
  const { dispatch: appDispatch, timeLeft, setCurrentUser, currentUser } = useAppContext();
  const [otp, setOtp] = useState<string>('');
  const pinRef = useRef<PinInput | null>(null);
  const { blackOne } = palette;
  const isDev = import.meta.env.NODE_ENV === 'development';

  const {
    primary: { main: primaryMain }
  } = palette;

  const onVerifyOtpSubmit = async () => {
    otp &&
      (await verifyOtp({
        variables: {
          verifyOtpInput: {
            otp,
            otpType: isTpin ? OtpVerification.TPinVerify : OtpVerification.PhoneVerify
          }
        }
      }));
  };

  const onChange = (value: string) => {
    setOtp(value);
  };

  const [verifyOtp, { loading: verifyOtpLoading }] = useVerifyOtpMutation({
    onError({ message }) {
      Alert.error(message);
      setOtp('');
      pinRef.current?.clear();
    },

    onCompleted(data) {
      const {
        verifyOtp: { phoneNumber }
      } = data;

      !resetPhone ? Alert.success(MSG_OTP_VERIFIED) : Alert.success(MSG_PHONE_NUMBER_UPDATED);

      if (currentUser) {
        const { status: userStatus, email, emailVerified, isSubscribed, isTPinAdded } = currentUser;

        if (!isTpin) {
          const updatedUser = { ...currentUser, status: userStatus, email, emailVerified, isSubscribed, isTPinAdded, phoneNumber: phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}` };
          setCurrentUser(updatedUser);
        }
      }
      handleNext?.();
      !isTpin && emailEditClose?.();
      isTpin ? appDispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: 60 }) : appDispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: import.meta.env.VITE_SEND_OTP_V2_TIME_LEFT ?? 300 });
    }
  });

  const onCompleteOtp = async (value: string) => {
    const otp = value;

    otp &&
      (await verifyOtp({
        variables: {
          verifyOtpInput: {
            otp,
            otpType: isTpin ? OtpVerification.TPinVerify : OtpVerification.PhoneVerify
          }
        }
      }));
  };

  const resendCode = async () => {
    clearOtp();
    isTpin ? appDispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: 60 }) : appDispatch({ type: ActionType.SET_TIME_LEFT, timeLeft: import.meta.env.VITE_SEND_OTP_V2_TIME_LEFT ?? 300 });

    if (displayPhoneNumber && isTpin) {
      const sendOtpV2TypeFunction = sendOtp as sendOtpV2Type;

      await sendOtpV2TypeFunction({
        variables: {
          sendOtpInputV2: {
            phoneNumber: displayPhoneNumber,
            otpType: OtpVerification.TPinVerify
          }
        }
      });
    }

    if (!isTpin && displayPhoneNumber) {
      const sendOtpTypeV2Function = sendOtp as sendOtpV2Type;

      await sendOtpTypeV2Function({
        variables: {
          sendOtpInputV2: {
            phoneNumber: displayPhoneNumber,
            otpType: OtpVerification.PhoneVerify
          }
        }
      });
    }
  };

  const clearOtp = () => {
    pinRef.current?.clear();
    setOtp('');
  };

  return (
    <>
      <Box display="flex" alignItems="flex-start">
        <Box flex="1">
          <PageHeading
            color={blackOne}
            title={ONBOARDING_ADD_INFO_STRINGS.TITLE_CHECK_PHONE}
            subTitle={(() => {
              const phone = displayPhoneNumber ?? '';
              const boldPhone = isTpin ? phone : `+${phone}`;
              if (isTpin) {
                const email = currentUser?.email ?? '';
                const text = ONBOARDING_ADD_INFO_STRINGS.CHECK_PHONE_SUBTITLE_TPIN(boldPhone, email);
                const [before, rest] = text.split(boldPhone);
                const [middle, after] = rest.split(email);
                return (
                  <>
                    {withTrademark(before)}
                    <span style={{ fontWeight: 'bold', color: primaryMain }}>{boldPhone}</span>
                    {withTrademark(middle)}
                    <span style={{ fontWeight: 'bold', color: primaryMain }}>{email}</span>
                    {withTrademark(after)}
                  </>
                );
              }
              const text = ONBOARDING_ADD_INFO_STRINGS.CHECK_PHONE_SUBTITLE_CALL(phone);
              const [before, after] = text.split(boldPhone);
              return <>{withTrademark(before)}<span style={{ fontWeight: 'bold', color: primaryMain }}>{boldPhone}</span>{withTrademark(after)}</>;
            })()}
          />
        </Box>

        {emailEditClose && (
          <IconButton onClick={emailEditClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {isTpin && (
        <>
          <CustomPinInput onChange={onChange} onComplete={onCompleteOtp} pinRef={pinRef} infoSteps={infoSteps} timer />

          <Button
            variant="contained"
            fullWidth
            onClick={async () => await onVerifyOtpSubmit()}
            disabled={verifyOtpLoading || timeLeft === 0}
            endIcon={verifyOtpLoading && <CircularProgress size={20} color="inherit" />}
          >
            {ONBOARDING_ADD_INFO_STRINGS.ADD_AND_VERIFY}
          </Button>
        </>
      )}

      {!isTpin && (
        <Box sx={{ textAlign: 'center', margin: 'auto' }}>
          <Typography variant="h4" pb="20px" sx={{ color: blackOne }}>
            {LABEL_VERIFICATION_CODE}
          </Typography>

          <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {code && (
              <Box fontWeight="bold" fontSize="56px" letterSpacing="15px" textAlign="center">
                {code}
              </Box>
            )}
          </Box>

          <Timer title={timeLeft === 0 ? MSG_VERIFICATION_CODE_EXPIRED : MSG_VERIFICATION_CODE_EXPIRES_IN} infoSteps={infoSteps!} />

          <Typography variant="subtitle2" sx={{ color: blackOne, fontStyle: 'italic', fontSize: '11px' }}>
            {NOTE_VERIFICATION_REFRESH_PAGE}
          </Typography>
        </Box>
      )}

      <Box display="flex" alignItems="center" justifyContent="center" mt="24px">
        <Typography variant="subtitle2" sx={{ mr: 1 }}>{MSG_DONT_HAVE_CODE}</Typography>
        <Button variant="text" sx={{ color: primaryMain, padding: '0', fontWeight: '400', fontSize: '14px' }} onClick={resendCode} disabled={timeLeft !== 0}>
          {BUTTON_TEXT_RESEND_CODE}
        </Button>
      </Box>

      {code && isTpin && isDev && (
        <MaterialAlert sx={{ marginTop: '24px' }} severity="info">
          <Typography variant="subtitle2" sx={{ color: blackOne }}>
            {MSG_DEV_OTP_NOTICE}{code}
          </Typography>
        </MaterialAlert>
      )}
    </>
  );
};
